import React from 'react';
import Paper from '@material-ui/core/Paper';
import { t } from 'ttag';
import useReactRouter from 'use-react-router';
import { useMutation } from '@apollo/react-hooks';
import { MTableToolbar } from 'material-table';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { Tooltip } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import useNotifier from '../../../components/Messages/hooks';
import { extractDataError } from '../../../utils/errors';
import {
  PROTOCOL_STATUS_ATHLETE_TITLE,
  RANK_PRIORITY_MAP,
  USER_TYPES,
} from '../../../utils/enums';
import { useNavigator } from '../../../components/Navigator';
import { formatRank, isIntersection } from '../../../utils/helpers';
import useUser from '../../../auth/hooks';
import useDataList from '../../../hooks/useDataList';
import useApi from '../../../api';
import DataTable from '../../../components/DataTable';
import useEditForm from '../../../hooks/useEditForm';
import MyChip from '../../../components/MyChip';
import Loading from '../../../components/Loading';

const getCurrentYear = () => new Date().getFullYear();

const ListAthleteProtocols = () => {
  const navigate = useNavigator();
  const { user } = useUser();
  const { match } = useReactRouter();
  const certificationsId = match.params.id;
  const { rank } = match.params;
  const { protocolId } = match.params;
  const api = useApi();
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const pushMessage = useNotifier();
  const { data, loading, onDelete, refetch } = useDataList({
    schema: api.schema.certifications_athlete,
    router: null,
    variables: {
      inputFilter: { certificationsId: Number(certificationsId), rank },
    },
  });
  const { dataCert, loading: certLoading } = useEditForm({
    schema: api.schema.certifications,
    router: null,
    variables: { inputFilter: { id: Number(certificationsId) } },
  });
  const [DoneAthleteRank] = useMutation(
    api.schema.certifications_athlete.done()[0],
    {
      onCompleted: (d) => {
        const { doneAthleteRank: res } = d;
        if (res.ok) {
          pushMessage({ text: 'Спортсмен здав!' });
          refetch();
        } else {
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );
  const [UnDoneAthleteRank] = useMutation(
    api.schema.certifications_athlete.undone()[0],
    {
      onCompleted: (d) => {
        const { undoneAthleteRank: res } = d;
        if (res.ok) {
          pushMessage({ text: 'Спортсмен не здав!' });
          refetch();
        } else {
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );

  const columns = [
    { title: t`Id`, field: 'id', editable: 'never' },
    {
      title: t`Імя атлета`,
      field: 'name',
      render: ({ athlete, contributions }) =>
        contributions.filter((c) => c.year === String(getCurrentYear()))
          .length !== 0 ? (
          (athlete && athlete.fullName) || ''
        ) : (
          <Tooltip title={t`Не здав членський внесок`}>
            <span>
              <MyChip
                label={athlete.fullName}
                color='#db0a0a'
                variant='outlined'
                onClick={() =>
                  navigate(`${api.router.athletes.edit(athlete.id)}?tab=1`)
                }
              />
            </span>
          </Tooltip>
        ),
    },
    {
      title: t`Кю`,
      field: 'rank',
      render: ({ rank }) => formatRank(rank) || '',
      customSort: (a, b) =>
        (RANK_PRIORITY_MAP[a.rank] || 0) - (RANK_PRIORITY_MAP[b.rank] || 0),
    },
    {
      title: t`Статус`,
      field: 'status',
      render: ({ statusStatementAthlete }) =>
        PROTOCOL_STATUS_ATHLETE_TITLE[statusStatementAthlete],
    },
    {
      title: t`Здача`,
      field: 'actions',
      render: (data) =>
        data.statusProtocol == 2 ||
        isIntersection(user.types, [USER_TYPES.trainer]) ? null : (
          <>
            <FormControlLabel
              value='start'
              control={
                <Switch
                  checked={data.statusStatementAthlete == 1}
                  onChange={() => {
                    data.statusStatementAthlete == 1
                      ? UnDoneAthleteRank({
                          variables: {
                            id: certificationsId,
                            athleteId: data.athleteId,
                            rank: data.rank,
                          },
                        })
                      : DoneAthleteRank({
                          variables: {
                            id: certificationsId,
                            athleteId: data.athleteId,
                            rank: data.rank,
                          },
                        });
                  }}
                  color='primary'
                />
              }
              labelPlacement='start'
            />
          </>
        ),
    },
  ];
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const [closeProtocol] = useMutation(
    api.schema.certifications_athlete.closeProtocol()[0],
    {
      onCompleted: (d) => {
        const { closeProtocol: res } = d;
        if (res.ok) {
          setOpen(false);
          pushMessage({ text: 'Протокол закритий' });
          refetch();
        } else {
          const dataError = extractDataError(res.errors);
          pushMessage({ text: dataError });
        }
      },
    }
  );
  const [generateProfileInfo] = useMutation(
    api.schema.certifications_athlete.generateProfileInfo()[0],
    {
      onCompleted: (d) => {
        const res =
          d[api.schema.certifications_athlete.generateProfileInfo()[1]];
        if (res.ok) {
          window.open(res.url, '_blank').focus();
        }
      },
    }
  );

  if (loading || certLoading) return <Loading />;
  const isProtocolClosed = data.every((r) => r.statusProtocol === 2);

  return (
    <Paper>
      <DataTable
        isLoading={loading}
        title={`Протокол №: ${protocolId} ${
          isProtocolClosed ? '(Закритий)' : ''
        }`}
        components={{
          Toolbar: (props) => (
            <div>
              <MTableToolbar {...props} />
              <div style={{ padding: '0px 10px' }}>
                <Tooltip title='Повернутись до списку протоколів'>
                  <IconButton
                    aria-label='delete'
                    onClick={() =>
                      navigate(
                        api.router.certifications.Protocol(certificationsId)
                      )
                    }
                  >
                    <KeyboardBackspaceIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ),
        }}
        columns={columns}
        onGeneratePdf={() =>
          generateProfileInfo({
            variables: {
              id: certificationsId,
              rank,
              protocolId,
            },
          })
        }
        data={data || []}
        // onEdit={(e, d) => navigate(api.router.certifications.editAthlete(certificationsId, d.id))}
        onDelete={
          isIntersection(user.types, [USER_TYPES.trainer]) ? null : onDelete
        }
        onCloseProtocol={isProtocolClosed ? null : handleClickOpen}
        isDeletableFn={(d) => !(dataCert.status == 2 || d.statusProtocol == 2)}
      />
      <div>
        <Dialog
          fullScreen={fullScreen}
          open={open}
          onClose={handleClose}
          aria-labelledby='responsive-dialog-title'
        >
          <DialogContent>
            <DialogContentText>
              Ви впевнені, що хочете відправити данні?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={handleClose} color='primary'>
              Відмінити
            </Button>
            <Button
              onClick={() =>
                closeProtocol({ variables: { id: certificationsId, rank } })
              }
              color='primary'
              autoFocus
            >
              Відправити
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Paper>
  );
};

export default ListAthleteProtocols;
